/**
 * Hook que gerencia o ciclo de vida de UMA sessão de treino do aluno:
 * carregar/iniciar sessão aberta, logar e desfazer exercícios, concluir e
 * salvar o feedback pós-treino.
 *
 * Estado interno: `session` (linha workout_sessions atual), `logs` (mapa
 * plan_exercise_id → SessionLog), `sessionLoading`.
 *
 * Decisão não-óbvia: `saveFeedback` faz insert direto (1 feedback por sessão,
 * UNIQUE em session_id). `observations` é texto livre opcional (null se vazio).
 */
import { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { WorkoutSession, SessionLog } from '@/lib/types';

export function useWorkoutSession() {
  const [session, setSession] = useState<WorkoutSession | null>(null);
  const [logs, setLogs] = useState<Record<string, SessionLog>>({});
  const [sessionLoading, setSessionLoading] = useState(true);

  async function loadSession(planId: string, studentId: string) {
    setSessionLoading(true);
    setSession(null);
    setLogs({});
    const { data: existing } = await supabase
      .from('workout_sessions')
      .select('*')
      .eq('plan_id', planId)
      .eq('student_id', studentId)
      .is('completed_at', null)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (existing) {
      setSession(existing as WorkoutSession);
      const { data: existingLogs } = await supabase
        .from('session_logs')
        .select('*')
        .eq('session_id', existing.id);
      if (existingLogs) {
        const map: Record<string, SessionLog> = {};
        existingLogs.forEach(l => { map[l.plan_exercise_id] = l as SessionLog; });
        setLogs(map);
      }
    }
    setSessionLoading(false);
  }

  async function startSession(planId: string, studentId: string) {
    const { data, error } = await supabase
      .from('workout_sessions')
      .insert({ plan_id: planId, student_id: studentId })
      .select()
      .single();
    if (error) throw error;
    setSession(data as WorkoutSession);
    return data as WorkoutSession;
  }

  async function logExercise(sessionId: string, planExerciseId: string, setsDone: number, repsDone: number, loadUsed: number | null) {
    const { data, error } = await supabase
      .from('session_logs')
      .insert({ session_id: sessionId, plan_exercise_id: planExerciseId, sets_done: setsDone, reps_done: repsDone, load_used: loadUsed })
      .select()
      .single();
    if (error) throw error;
    setLogs(prev => ({ ...prev, [planExerciseId]: data as SessionLog }));
  }

  async function undoLog(planExerciseId: string) {
    const log = logs[planExerciseId];
    if (!log) return;
    const { error } = await supabase.from('session_logs').delete().eq('id', log.id);
    if (error) throw error;
    setLogs(prev => { const next = { ...prev }; delete next[planExerciseId]; return next; });
  }

  async function completeSession(sessionId: string) {
    const { error } = await supabase
      .from('workout_sessions')
      .update({ completed_at: new Date().toISOString() })
      .eq('id', sessionId);
    if (error) throw error;
    setSession(prev => prev ? { ...prev, completed_at: new Date().toISOString() } : null);
  }

  async function saveFeedback(
    sessionId: string,
    data: { intensity: number; fatigue: number; had_pain: boolean; pain_location: string | null; observations: string | null }
  ) {
    const { error } = await supabase
      .from('workout_feedback')
      .insert({ session_id: sessionId, ...data });
    if (error) throw error;
  }

  return { session, logs, sessionLoading, loadSession, startSession, logExercise, undoLog, completeSession, saveFeedback };
}
